import { useCallback, useMemo, useRef } from 'react';
import { useApolloClient } from '@apollo/client';
import { useModals, ModalNames } from 'components/modals';
import useUI from 'hooks/use-ui';
import useUser from 'hooks/use-user';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import { InterfaceVariants } from 'src/constants/interface-variants';
import { usePhoneVerification } from 'src/components/modals/phone-verification-nudge/use-phone-verification';
import { deriveNudgeLeg } from 'src/components/modals/phone-verification-nudge/phone-verification.types';
import type {
  NudgeOutcome,
  PhoneVerificationNudgeContext,
} from 'src/components/modals/phone-verification-nudge/phone-verification.types';
import { usePhoneVerificationNudgeOperational } from './use-phone-verification-nudge-operational';

// Deliberately narrower than use-post-signup-modal-sequence's EXCLUDED_VARIANTS:
// `mobileEcomm` and `embedded` are NOT excluded here. The nudge is meant to reach the
// mobile-ecomm webview (it's one of the primary surfaces called out in the ticket) and the
// embedded iframe (Google/Apple popup + postMessage still round-trips through the same
// orchestrator). Only kiosk and plus -- surfaces with no concept of an individual login-owned
// phone number -- are excluded.
const EXCLUDED_VARIANTS: InterfaceVariants[] = [InterfaceVariants.kiosk, InterfaceVariants.plus];

export type TriggerPhoneVerificationNudgeParams = { viaMarker?: boolean };

export type UsePhoneVerificationNudgeResult = {
  triggerPhoneVerificationNudge: (params?: TriggerPhoneVerificationNudgeParams) => Promise<void>;
};

function noop(): void {
  // Intentional no-op for a swallowed clearStore() rejection -- see use-user-button.ts:162.
}

/**
 * Post-login orchestrator for the phone-verification "Nudge" modal. Modelled line-for-line on
 * `use-post-signup-modal-sequence.ts`: same re-entrancy-ref guard, same try/finally shape, same
 * `useCallback`/`useMemo` return contract. Fetches `phoneVerificationStatus`, derives which leg
 * (if any) to show via `deriveNudgeLeg`, opens the modal, and handles its terminal outcome.
 *
 * Every failure mode here is silent by design -- a status-fetch error, an already-active modal,
 * or a suppressed surface must never produce a toast, a console entry, or any other user-visible
 * artifact. The nudge has zero persistence: skipping a landing simply means the next login
 * re-runs this from scratch.
 */
export function usePhoneVerificationNudge(): UsePhoneVerificationNudgeResult {
  const Modals = useModals();
  const { openModal } = Modals;
  const UI = useUI();
  const User = useUser();
  const { dispensary } = useDispensary();
  const apolloClient = useApolloClient();
  const { fetchStatus } = usePhoneVerification();
  const isNudgeInFlightRef = useRef(false);
  const dispensaryId = dispensary?.id ?? null;
  // FINTECH-4632: always dispensary-derived, never present without a dispensaryId alongside it --
  // this is caller-supplied LD targeting context only, not a new authorization surface.
  const enterpriseId = dispensary?.retailer?.enterpriseId ?? null;
  const nudgeOperational = usePhoneVerificationNudgeOperational();

  const handleLoginToOtherAccount = useCallback(async (): Promise<void> => {
    // Order matters: logout first so no authenticated refetch races the cache clear;
    // clearStore (not resetStore) so nothing eagerly refetches; login modal last, on a
    // clean store. See PLAN §5.2.
    User.logout();
    await apolloClient.clearStore().catch(noop);
    void openModal(ModalNames.login);
  }, [User, apolloClient, openModal]);

  const triggerPhoneVerificationNudge = useCallback(
    async (_params: TriggerPhoneVerificationNudgeParams = {}): Promise<void> => {
      // Every guard below (including the in-flight/active-modal checks) now lives INSIDE the
      // try: `triggerPhoneVerificationNudge` is called as bare `void trigger()` at every call
      // site (no `.catch`), so a synchronous throw from a guard -- e.g. an unexpectedly
      // undefined `User`/`UI`/`Modals` -- must be swallowed by the same silent `catch` below
      // rather than escaping as an unhandled promise rejection. The single `finally` covers
      // every exit path (guard bail, thrown error, or a normal completion) so
      // `isNudgeInFlightRef` can never be left stuck `true` by a path that skips resetting it.
      try {
        // 1. Magic Login claim continuation slots in HERE, ahead of the nudge.
        //    if (await runClaimContinuation()) return;

        // 2. Nudge -- suppression gate. Bails are evaluated, and silent, in exactly this order:
        //    (1) no session, (2) LD killswitch (ecomm.phone-identity.operational -- the same
        //    circuit breaker Armageddon's PhoneNumbers::VerificationGate checks server-side;
        //    it only ever avoids the `status` network call, it does not make any rollout
        //    decision -- that's Armageddon's job, targeted per-user), (3) checkout page,
        //    (4) excluded surface, (5) already in flight, (6) a modal is already active (cheap
        //    pre-check; re-checked again below, immediately before opening -- see there).
        if (!User.isLoggedIn) {
          return;
        }
        if (!nudgeOperational) {
          return;
        }
        if (UI.isCheckoutPage) {
          return;
        }
        if (EXCLUDED_VARIANTS.includes(UI.variant)) {
          return;
        }
        if (isNudgeInFlightRef.current) {
          return;
        }
        // (5) Modal collision (cheap short-circuit): openModal() never resolves for a name that's
        // already active (it just console.errors and returns), so an already-open modal of any
        // name must skip this landing entirely rather than risk hanging forever. There's no
        // persistence to lose by skipping -- the next login re-runs status from scratch. This is
        // only a pre-check: `fetchStatus` below is a network round-trip, during which an unrelated
        // modal (age gate, order-type takeover, announcement) can open, so we re-check immediately
        // before `openModal` as well -- see below.
        if (Modals.activeModals.size > 0) {
          return;
        }

        isNudgeInFlightRef.current = true;

        const { phoneVerificationStatus } = await fetchStatus(dispensaryId, enterpriseId);
        const leg = deriveNudgeLeg(phoneVerificationStatus?.status, phoneVerificationStatus?.hasPhone);

        if (leg === 'none') {
          return;
        }

        // Re-check for a modal collision immediately before opening: the check above ran before
        // `fetchStatus`'s network round-trip, during which some unrelated modal (age gate,
        // order-type takeover, announcement) may have opened. Bail silently rather than stacking
        // the nudge on top of it -- same zero-persistence reasoning as the early check.
        if (Modals.activeModals.size > 0) {
          return;
        }

        const context: PhoneVerificationNudgeContext = {
          initialLeg: leg,
          ownerEmailHint: phoneVerificationStatus?.ownerEmailHint ?? null,
          dispensaryId,
          enterpriseId,
        };

        const outcome = await openModal<PhoneVerificationNudgeContext, NudgeOutcome>(
          ModalNames.phoneVerificationNudge,
          context
        );

        if (outcome.kind === 'login_other_account') {
          await handleLoginToOtherAccount();
        }
      } catch {
        // Silent -- a failed status fetch (or any other unexpected error) must never surface.
      } finally {
        isNudgeInFlightRef.current = false;
      }
    },
    [User, UI, Modals, fetchStatus, dispensaryId, enterpriseId, nudgeOperational, openModal, handleLoginToOtherAccount]
  );

  return useMemo(() => ({ triggerPhoneVerificationNudge }), [triggerPhoneVerificationNudge]);
}
